import React from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const baseClass =
  'inline-flex items-center justify-center font-medium rounded-xl transition-all ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-sand-50 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]';

const variantClass: Record<Variant, string> = {
  primary:
    'bg-sage-600 text-white hover:bg-sage-700 shadow-card',
  secondary:
    'bg-white text-sand-800 border border-sand-200 hover:border-sand-300 hover:bg-sand-50 shadow-card',
  ghost:
    'bg-transparent text-sand-700 hover:bg-sand-100 hover:text-sand-900',
  danger:
    'bg-white text-red-700 border border-red-100 hover:bg-red-50',
  subtle:
    'bg-sage-50 text-sage-800 hover:bg-sage-100',
};

const sizeClass: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-base gap-2',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className = '',
  children,
  disabled,
  ...rest
}) => {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={[
        baseClass,
        variantClass[variant],
        sizeClass[size],
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
    >
      {loading ? (
        <Spinner />
      ) : (
        leftIcon && <span className="shrink-0 flex">{leftIcon}</span>
      )}
      {children}
      {rightIcon && !loading && <span className="shrink-0 flex">{rightIcon}</span>}
    </button>
  );
};

const Spinner: React.FC = () => (
  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
    <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: 'default' | 'danger';
}

export const IconButton: React.FC<IconButtonProps> = ({
  label,
  variant = 'default',
  className = '',
  children,
  ...rest
}) => (
  <button
    {...rest}
    aria-label={label}
    title={label}
    className={[
      'inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500/40',
      variant === 'danger'
        ? 'text-sand-500 hover:text-red-600 hover:bg-red-50'
        : 'text-sand-500 hover:text-sand-900 hover:bg-sand-100',
      className,
    ].join(' ')}
  >
    {children}
  </button>
);
